import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  TextField,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DigitalClock } from "@mui/x-date-pickers/DigitalClock";
import { Dayjs } from "dayjs";
import { useUpadateAvailabilityMutation } from "lib/features/availability/availabilityService";
import { Availability, AvailabilityDTO } from "lib/features/availability/types";
import { Item } from "lib/features/item/types";
import { useEffect, useState } from "react";
import { availabilityModalStyles } from "styles/AvailabilityDirectory/availabilityModal.styles";
import {
  getEndTime,
  getFormattedTime,
  replaceDatePart,
} from "util/dateFunctions";
import CrossIcon from "../../../assets/icons/cross.svg";
import AvailabilityDatePicker from "./AvailabilityDatePicker";

interface EditAvailabilityModalProps {
  open: boolean;
  close: () => void;
  customStyle?: object;
  sx?: object;
  selectedId: number | undefined;
  selectedAvailability: Availability;
  item: Item;
  setItem: any;
}

export interface Duration {
  label: string;
  value: number;
}

export default function EditAvailabilityModal({
  open,
  close,
  customStyle = {},
  sx = {},
  selectedId,
  selectedAvailability,
  item,
  setItem,
}: EditAvailabilityModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const [availability, setAvailability] = useState<Availability>();
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [selectedDuration, setSelectedDuration] = useState<number>(0);
  const [quantity, setQuantity] = useState<number | string>("");

  const [upadateAvailability, { isLoading }] = useUpadateAvailabilityMutation();

  const handleTimeChange = (time: Dayjs | null) => {
    if (!time) return;
    const updatedDate = new Date(selectedDate);
    updatedDate.setHours(time.hour());
    updatedDate.setMinutes(time.minute());
    setStartTime(updatedDate);
  };

  const handleDurationChange = (duration: number) => {
    setSelectedDuration(duration);
  };

  const calculateDuration = (startTime: Date, endTime: Date): number => {
    const timeDifference =
      (endTime.getTime() - startTime.getTime()) / (1000 * 3600); // Difference in hours

    if (timeDifference < 0) {
      // Adjust the end time if the time difference is negative (crosses midnight)
      const correctedEndTime = new Date(endTime);
      correctedEndTime.setDate(correctedEndTime.getDate() + 1); // Move to the next day
      return calculateDuration(startTime, correctedEndTime);
    }

    return timeDifference;
  };

  const onEdit = async () => {
    if (!selectedId || !availability) return;

    const endTime = getEndTime(startTime, selectedDuration);

    const availabilityToAdd: AvailabilityDTO = {
      item_id: selectedId,
      start_time: replaceDatePart(startTime, selectedDate),
      end_time: replaceDatePart(endTime, selectedDate),
      private: availability?.private,
      quantity: Number(quantity) ?? 0,
    };

    await upadateAvailability({
      id: availability?.id,
      availability: availabilityToAdd,
    });

    const updatedAvailabilities = item.availabilities.map((avail) => {
      if (avail.id === availability?.id) {
        return {
          ...avail,
          ...availabilityToAdd,
        };
      }
      return avail;
    });
    setItem({ ...item, availabilities: updatedAvailabilities });
    close();

    try {
    } catch (error) {
      console.log("error: ", error);
    }
  };

  useEffect(() => {
    const startTime = new Date(selectedAvailability?.start_time);
    const endTime = new Date(selectedAvailability?.end_time);

    setAvailability(selectedAvailability);

    setSelectedDate(startTime);
    setStartTime(startTime);

    const duration = calculateDuration(startTime, endTime);
    setSelectedDuration(duration); // Set the calculated duration

    setQuantity(selectedAvailability.quantity);
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={close}
      aria-labelledby="customer-modal-title"
      aria-describedby="customer-modal-description"
      sx={{ ...sx }}
    >
      <Box sx={{ ...availabilityModalStyles.modalStyles, ...customStyle }}>
        <Box sx={availabilityModalStyles.editHeader}>
          {/* Modal Title */}
          <Typography sx={availabilityModalStyles.headerText} textAlign="center" width="100%">
            Quick Availability Edit
          </Typography>

          {/* Cross */}
          <img
            src={CrossIcon}
            width={16}
            height={16}
            onClick={close}
            style={{ cursor: "pointer" }}
          />
        </Box>

        {/* Modal Body */}
        <Box mt={2}>
          <Box>
            <Typography sx={availabilityModalStyles.editModalHeading} textAlign="center">
              Date
            </Typography>
            <AvailabilityDatePicker
              selectedDate={selectedDate}
              handleDateSelect={(date: Date) => {
                setSelectedDate(date);
              }}
            />
          </Box>

          <Box sx={availabilityModalStyles.editBodyContainer}>
            <Box sx={availabilityModalStyles.editSingleContainer}>
              <Typography sx={availabilityModalStyles.editModalHeading}>
                Start Time *
              </Typography>

              <Box>
                <Button
                  sx={availabilityModalStyles.timePickerButton}
                  endIcon={<KeyboardArrowDown sx={{ color:"#000000", fontSize:"34px" }}  />}
                  onClick={() => {
                    setShowPicker(true);
                  }}
                >
                  <Typography style={availabilityModalStyles.timePickerText}>
                    {getFormattedTime(startTime)}
                  </Typography>
                </Button>

                {/* Modal with DigitalClock */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Dialog
                    open={showPicker}
                    onClose={() => setShowPicker(false)}
                    PaperProps={{
                      sx: availabilityModalStyles.timePickerDialog,
                    }}
                  >
                    <Box sx={availabilityModalStyles.timePickerContainer}>
                      <Typography
                        variant="h6"
                        sx={availabilityModalStyles.timePickerHeaderText}
                      >
                        Set Time
                      </Typography>

                      <Box sx={availabilityModalStyles.timePickerScrollable}>
                        <DigitalClock
                          // value={selectedTime}
                          onChange={(newValue: Dayjs | null) => {
                            handleTimeChange(newValue);
                            setShowPicker(false);
                          }}
                          ampm={true} // ✅ 12-hour format with AM/PM
                          timeStep={15}
                        />
                      </Box>

                      <Box sx={availabilityModalStyles.timePickerActions}>
                        <Button
                          variant="outlined"
                          onClick={() => setShowPicker(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => setShowPicker(false)}
                        >
                          Confirm
                        </Button>
                      </Box>
                    </Box>
                  </Dialog>
                </LocalizationProvider>
              </Box>
            </Box>
            <Box sx={availabilityModalStyles.editSingleContainer}>
              <Typography sx={availabilityModalStyles.editModalHeading}>
                Quantity *
              </Typography>
              <TextField
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                type="number"
                fullWidth
              />
            </Box>
          </Box>
        </Box>

        {/* Modal Footer */}
        <Box
          pt={2}
          sx={[
            availabilityModalStyles.modalFooter,
            { justifyContent: "flex-start", flexDirection: "column", alignItems: "center" },
          ]}
        >
          <Typography sx={availabilityModalStyles.footerText}>
            Item details/pricing can't be edited here. <span style={{ fontWeight: "600" }}>Create a new item</span> or <span style={{ fontWeight: "600" }}>duplicate and edit.</span>
          </Typography>
          <Button sx={availabilityModalStyles.greenEditButton} onClick={onEdit}>
            {isLoading ? (
              <CircularProgress
                sx={availabilityModalStyles.circularProgress}
                size={28}
              />
            ) : (
              "Save"
            )}
          </Button>

        </Box>
      </Box>
    </Modal>
  );
}
